import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Package, Search, Pencil, Loader2, RefreshCw, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Stocks() {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editStock, setEditStock] = useState(null);
  const [editQty, setEditQty] = useState(0);
  const [editNotes, setEditNotes] = useState("");

  const fetchStocks = async () => {
    try { setStocks((await axios.get(`${API}/stocks`)).data); }
    catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { fetchStocks(); }, []);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/stocks/${editStock.part_id}`, { quantity: editQty, notes: editNotes });
      toast.success("Stock atualizado!");
      setEditStock(null); fetchStocks();
    } catch (e) { toast.error("Erro"); }
  };

  const filtered = stocks.filter(s =>
    (s.part_code || "").toLowerCase().includes(search.toLowerCase()) ||
    (s.part_description || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalParts = stocks.length;
  const totalStock = stocks.reduce((s, r) => s + (r.quantity || 0), 0);
  const zeroStock = stocks.filter(s => (s.quantity || 0) <= 0).length;

  const handleExportPdf = () => {
    const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/logo-ep3d.png`;
    const now = new Date().toLocaleString("pt-PT");
    const rows = filtered.map(s => `<tr><td style="font-family:monospace;font-weight:bold">${s.part_code||"-"}</td><td>${s.part_description||"-"}</td><td style="text-align:right;font-family:monospace;font-weight:bold;font-size:14px">${(s.quantity||0).toLocaleString()}</td><td>${(s.quantity||0)>100?"Normal":(s.quantity||0)>0?"Baixo":"Sem Stock"}</td><td>${s.updated_at?new Date(s.updated_at).toLocaleString("pt-PT",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}):"-"}</td></tr>`).join("");
    const html = `<!DOCTYPE html><html><head><title>Armazém - Stock Atual</title>
    <style>@page{size:A4;margin:12mm} body{font-family:Arial,sans-serif;color:#1e293b;font-size:11px}
    .header{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #f59e0b;padding-bottom:10px;margin-bottom:15px}
    .header img{height:50px} h1{font-size:18px;color:#0f172a;margin:0 0 5px}
    .summary{display:flex;gap:20px;margin-bottom:15px;font-size:12px}
    .summary div{background:#f8fafc;padding:8px 16px;border-radius:6px;text-align:center}
    .summary strong{font-size:18px;display:block}
    table{width:100%;border-collapse:collapse} th{background:#0f172a;color:#f59e0b;padding:6px 10px;text-align:left;font-size:10px}
    td{padding:5px 10px;border-bottom:1px solid #e5e7eb;font-size:10px}
    .footer{margin-top:20px;border-top:1px solid #e5e7eb;padding-top:8px;color:#9ca3af;font-size:9px;text-align:center}
    </style></head><body>
    <div class="header"><img src="${logoUrl}" alt="EP3D" onerror="this.outerHTML='<b>EP3D Produção</b>'" /><div style="text-align:right;font-size:10px"><strong>Relatório de Stock</strong><br>${now}</div></div>
    <h1>Armazém de Peças — Stock Atual</h1>
    <div class="summary"><div>Total Peças<strong>${totalParts}</strong></div><div>Stock Total<strong>${totalStock.toLocaleString()}</strong></div><div>Sem Stock<strong>${zeroStock}</strong></div></div>
    <table><tr><th>Código</th><th>Descrição</th><th style="text-align:right">Quantidade</th><th>Estado</th><th>Última Atualização</th></tr>${rows}</table>
    <div class="footer">EP3D Produção — Relatório de Stock — ${now}</div></body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `Armazem_Stock_${new Date().toISOString().split("T")[0]}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="armazem-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-chivo text-3xl font-bold text-foreground flex items-center gap-3"><Package className="h-7 w-7" /> Armazém de Peças</h1>
          <p className="text-muted-foreground mt-1">Controlo de stock de peças produzidas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchStocks}><RefreshCw className="h-4 w-4 mr-2" /> Atualizar</Button>
          <Button className="gap-2 bg-amber-500 hover:bg-amber-600" onClick={handleExportPdf}><FileText className="h-4 w-4" /> Exportar PDF</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-muted-foreground">Total Peças</p><p className="text-3xl font-bold">{totalParts}</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-green-600">Stock Total</p><p className="text-3xl font-bold text-green-600">{totalStock.toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-red-600">Sem Stock</p><p className="text-3xl font-bold text-red-600">{zeroStock}</p></CardContent></Card>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Pesquisar peça..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Package className="h-4 w-4" /> Stock Atual</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-right">Quantidade em Stock</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Última Atualização</TableHead>
              <TableHead>Último Ajuste</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Sem stocks registados. As peças boas entram automaticamente em stock.</TableCell></TableRow>
              ) : filtered.map(s => (
                <TableRow key={s.part_id}>
                  <TableCell className="font-mono font-bold">{s.part_code || "-"}</TableCell>
                  <TableCell>{s.part_description || "-"}</TableCell>
                  <TableCell className="text-right font-mono text-2xl font-bold">{(s.quantity || 0).toLocaleString()}</TableCell>
                  <TableCell>
                    {(s.quantity || 0) > 100 ? <Badge className="bg-green-500 text-white">Normal</Badge>
                    : (s.quantity || 0) > 0 ? <Badge className="bg-amber-500 text-white">Baixo</Badge>
                    : <Badge className="bg-red-500 text-white">Sem Stock</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.updated_at ? new Date(s.updated_at).toLocaleString("pt-PT", {day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}) : "-"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{s.last_adjusted_by ? `${s.last_adjusted_by}: ${s.last_adjustment_notes || ""}` : "-"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditStock(s); setEditQty(s.quantity || 0); setEditNotes(""); }}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editStock} onOpenChange={() => setEditStock(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar Stock — {editStock?.part_code}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-slate-50 rounded p-3"><p className="text-sm">{editStock?.part_description}</p><p className="text-xs text-muted-foreground">Stock atual: <strong>{editStock?.quantity}</strong></p></div>
            <div className="space-y-2"><Label>Nova Quantidade</Label><Input type="number" min="0" value={editQty} onChange={e => setEditQty(parseInt(e.target.value) || 0)} className="font-mono text-lg" /></div>
            <div className="space-y-2"><Label>Motivo do Ajuste</Label><Input value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Ex: Inventário, Correção, Expedição manual..." /></div>
            <Button className="w-full" onClick={handleSave}>Guardar Stock</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
